import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus } from 'lucide-react';
import { authService } from '../../services/authService';
import Button from '../../components/ui/Button';
import { teacherService } from '../../services/teacherService';
import { authentication, host } from '../../api/ApiCalls';
import axios from 'axios';
import { toast } from 'react-toastify';
import { tostifyOption } from '../../utils/toastOptions';
import { interests } from '../../utils/interests';

const Signup = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'student',
    image: null,
  });
  const [image, setImage] = useState(null); // Image file ke liye state
  const [preview, setPreview] = useState(null); // Preview image ke liye state
  const [degreePreview, setDegreePreview] = useState(null); // Preview image ke liye state
  const [degreeImage, setDegreeImage] = useState(null); // Image file ke liye state
  const [error, setError] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const navigate = useNavigate();
  const [emailError, setEmailError] = useState('');
  const [createAccountLoading, setCreateAccountLoading] = useState(false);


  const handleEmailChange = (e) => {
    const email = e.target.value;
    const emailPattern =
      /^[a-zA-Z][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    setFormData({ ...formData, email });

    // Live validation
    if (email.length > 0 && !emailPattern.test(email)) {
      if (/^[0-9]/.test(email)) {
        setEmailError('Email must start with an alphabet.');
      } else {
        setEmailError('Please enter a valid email address.');
      }
    } else {
      setEmailError(''); // No error
    }
  };

  const toggleInterest = (interest) => {
    if (selectedInterests.includes(interest)) {
      setSelectedInterests(selectedInterests.filter((i) => i !== interest));
    } else if (selectedInterests.length < 5) {
      setSelectedInterests([...selectedInterests, interest]);
    } else {
      toast.error('You can select up to 5 interests only.', tostifyOption);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      console.error('❌ No file selected!');
      return;
    }
    setImage(file);
    console.log('📸 Selected Image:', file); // ✅ Debugging: Check Selected Image
  };

  const handleDegreeImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      console.error('❌ No file selected!');
      return;
    }
    setDegreeImage(file);
    console.log('📸 Selected Degree Image:', file); // ✅ Debugging: Check Selected Image
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreateAccountLoading(true);
    console.log('🚀 handleSubmit function called!');

    const emailPattern =
      /^[a-zA-Z][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const namePattern = /^[A-Za-z\s]+$/;
    const usernamePattern = /^[A-Za-z]+[A-Za-z0-9_]*$/;
    const passwordPattern =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    const { name, email, username, password, role } = formData;

    // ❌ Check required fields
    if (!name || !email || !username || !password) {
      toast.error('Please fill in all required fields.', tostifyOption);
      setCreateAccountLoading(false);
      return;
    }

    // ❌ Name Validation
    if (!namePattern.test(name)) {
      toast.error('Name must contain only letters and spaces.', tostifyOption);
      setCreateAccountLoading(false);
      return;
    }

    // ❌ Email Validation
    if (!emailPattern.test(email)) {
      toast.error(
        'Email must start with a letter and be a valid format.',
        tostifyOption
      );
      setCreateAccountLoading(false);
      return;
    }

    // ❌ Username Validation
    if (!usernamePattern.test(username)) {
      toast.error(
        'Username must start with a letter and contain only letters, numbers or underscores.',
        tostifyOption
      );
      setCreateAccountLoading(false);
      return;
    }

    // ❌ Password Strength Check
    if (!passwordPattern.test(password)) {
      toast.error(
        'Password must have at least 8 chars, 1 uppercase, 1 number, and 1 special character.',
        tostifyOption
      );
      setCreateAccountLoading(false);
      return;
    }

    // ❌ Image Validation
    if (!image) {
      toast.error('Please select a profile image.', tostifyOption);
      setCreateAccountLoading(false);
      return;
    }

    if (role === 'teacher' && !degreeImage) {
      toast.error('Please upload a degree or certificate.', tostifyOption);
      setCreateAccountLoading(false);
      return;
    }

    // ✅ All Valid – Submit
    try {
      const res = await axios.post(`${host}/api/auth/check-availability`, {
        email: email,
        username: username,
      });

      console.log('✅ Availability check passed', res.data);

      navigate('/verify-otp', {
        state: {
          name,
          username,
          email,
          password,
          role,
          image,
          degreeImage,
          interests: selectedInterests,
        },
      });
      setCreateAccountLoading(false);
    } catch (err) {
      const message = err.response?.data?.message || 'An error occurred';
      console.error('❌', message);
      setError(message);
      toast.error(message, tostifyOption);
      setCreateAccountLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <UserPlus className="mx-auto h-12 w-12 text-indigo-600" />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create your account
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 p-3 rounded">
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="role"
                className="block text-sm font-medium text-gray-700"
              >
                I want to
              </label>
              <select
                id="role"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
              >
                <option value="student">Learn as a Student</option>
                <option value="teacher">Teach as an Instructor</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Profile Image
              </label>
              <input
                type="file"
                accept="image/*"
                className="mt-1 block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4 file:rounded-md
                  file:border-0 file:text-sm file:font-semibold
                  file:bg-indigo-100 file:text-indigo-700
                  hover:file:bg-indigo-200"
                onChange={handleImageChange}
              />
              {preview && (
                <img
                  src={preview}
                  alt="Preview"
                  className="mt-2 w-24 h-24 rounded-full object-cover"
                />
              )}
            </div>
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                Full Name
              </label>
              <input
                id="name"
                type="text"
                required
                pattern="[A-Za-z\s]+"
                title="Only alphabets are allowed"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>

            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700"
              >
                Username
              </label>
              <input
                id="username"
                type="text"
                required
                pattern="^[A-Za-z]+[A-Za-z0-9_]*$"
                title="Username should start with alphabet and can contain underscore or number in it"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                title="Email must start with a letter and be valid"
                pattern="^[a-zA-Z][a-zA-Z0-9._%+-]*@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$"
                className={`mt-1 block w-full rounded-md  ${
                  emailError ? 'border-red-500' : 'border-gray-300'
                } shadow-sm focus:border-indigo-500 focus:ring-indigo-500`}
                value={formData.email}
                onChange={handleEmailChange}
              />
              {emailError && (
                <p className="mt-1 text-sm text-red-600">{emailError}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                minLength={8}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
              />
            </div>
            {/* if formData.role === "teacher" then give the upload option of his degree or any authenticated certificate */}

            {formData.role === 'teacher' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Upload your Degree or Certificate
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="mt-1 block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4 file:rounded-md
                  file:border-0 file:text-sm file:font-semibold
                  file:bg-indigo-100 file:text-indigo-700
                  hover:file:bg-indigo-200"
                  onChange={handleDegreeImageChange}
                />
                {degreePreview && (
                  <img
                    src={degreePreview}
                    alt="Preview"
                    className="mt-2 w-24 h-24 rounded-full object-cover"
                  />
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Your Interests{' '}
                <span className="text-xs text-gray-400">(max 5)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {interests.map((interest) => (
                  <button
                    type="button"
                    key={interest}
                    className={`px-3 py-1 rounded-full text-sm border ${
                      selectedInterests.includes(interest)
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-gray-100 text-gray-700 border-gray-300'
                    }`}
                    onClick={() => toggleInterest(interest)}
                  >
                    {interest}
                  </button>
                ))}
              </div>
              {selectedInterests.length > 0 && (
                <div className="mt-2 text-xs text-gray-500">
                  Selected: {selectedInterests.join(', ')}
                </div>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={createAccountLoading}>
              {createAccountLoading ? (
    <div className="flex items-center justify-center gap-2">
      <svg
        className="animate-spin h-5 w-5 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        ></circle>
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
        ></path>
      </svg>
      Creating...
    </div>
  ) : (
    "Create Account"
  )}
            </Button>
          </form>

          <div className="mt-6">
            <p className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
